import { useCallback, memo } from 'react';
import { LuChartBar, LuChevronDown, LuChevronRight, LuInfo, LuTriangleAlert } from 'react-icons/lu';
import {
	SidebarSection,
	SectionTitle,
	SectionContent,
	FormGroup,
	SettingsGrid,
	ToggleGroup,
	Toggle,
	AdvancedToggle,
	FlightStatsCard,
	StatsGrid,
	StatItem,
	HighlightStat,
	WarningHint,
} from './FlightPlannerStyles';
import ProjectInfoSection from './ProjectInfoSection';
import AiModelSection from './AiModelSection';
import { DRONE_MODELS, BATTERY_FLIGHT_TIME_MIN } from '../constants/flightConstants';
import { FEATURE_FLAGS } from '@constants/featureFlags';

/**
 * Step 3 (Orthomosaic) — Flight configuration panel.
 * Drone model, template, flight parameters, and AI model selection.
 */
const WorkflowSetup = ({
	projectSettings,
	onSettingsChange,
	detectedMunicipality,
	flightTemplates,
	onTemplateChange,
	flightStats,
	availableAiModels,
	showAdvancedSettings,
	onToggleAdvancedSettings,
}) => {
	const currentTemplate = flightTemplates[projectSettings.flightTemplate];

	const updateField = useCallback(
		(field, value) => onSettingsChange(prev => ({ ...prev, [field]: value })),
		[onSettingsChange]
	);

	return (
		<>
			<ProjectInfoSection
				projectSettings={projectSettings}
				onSettingsChange={onSettingsChange}
				detectedMunicipality={detectedMunicipality}
			/>

			<SidebarSection>
				<SectionTitle>Flight Template</SectionTitle>
				<SectionContent>
					<FormGroup>
						<label>
							Drone Model: <span style={{ color: '#ff6b6b' }}>*</span>
						</label>
						<select
							value={projectSettings.droneModel}
							onChange={e => updateField('droneModel', e.target.value)}
							style={{
								borderColor: !projectSettings.droneModel ? '#ff6b6b40' : undefined,
							}}>
							<option value="">Select drone...</option>
							{DRONE_MODELS.map(drone => (
								<option key={drone.id} value={drone.id}>
									{drone.name}
								</option>
							))}
						</select>
					</FormGroup>

					<FormGroup>
						<label>Template:</label>
						<select
							value={projectSettings.flightTemplate}
							onChange={e => onTemplateChange(e.target.value)}>
							{Object.entries(flightTemplates).map(([id, template]) => (
								<option key={id} value={id}>
									{template.name}
								</option>
							))}
						</select>
						<small style={{ color: '#888', fontSize: '0.8rem' }}>
							{currentTemplate?.description}
						</small>
					</FormGroup>

					{flightStats && (
						<FlightStatsCard>
							<h4>
								<LuChartBar
									size={14}
									style={{ verticalAlign: 'middle', marginRight: '4px' }}
								/>{' '}
								Flight Estimate
							</h4>
							<HighlightStat>
								<div className="stat-label">Estimated Flight Time</div>
								<div className="stat-value">
									{flightStats.flightTime}
									<span className="stat-unit"> min</span>
								</div>
							</HighlightStat>
							<StatsGrid>
								<StatItem>
									<div className="stat-label">GSD</div>
									<div className="stat-value">
										{flightStats.gsd}
										<span className="stat-unit">cm/px</span>
									</div>
								</StatItem>
								<StatItem>
									<div className="stat-label">Photos</div>
									<div className="stat-value">{flightStats.totalPhotos}</div>
								</StatItem>
								<StatItem>
									<div className="stat-label">Flight Lines</div>
									<div className="stat-value">{flightStats.numLines}</div>
								</StatItem>
								<StatItem>
									<div className="stat-label">Distance</div>
									<div className="stat-value">
										{flightStats.totalDistance}
										<span className="stat-unit">km</span>
									</div>
								</StatItem>
								<StatItem>
									<div className="stat-label">Est. Batteries</div>
									<div className="stat-value">
										{Math.ceil(
											parseFloat(flightStats.flightTime) /
												BATTERY_FLIGHT_TIME_MIN
										)}
									</div>
								</StatItem>
							</StatsGrid>
							<div
								style={{
									marginTop: '0.75rem',
									padding: '0.5rem',
									background: 'rgba(255, 193, 7, 0.1)',
									borderRadius: '4px',
									fontSize: '0.7rem',
									color: '#ffc107',
								}}>
								<LuInfo
									size={12}
									style={{ verticalAlign: 'middle', marginRight: '4px' }}
								/>
								Based on {BATTERY_FLIGHT_TIME_MIN} min flight time per battery. Does
								not include takeoff or return to landing.
							</div>
						</FlightStatsCard>
					)}

					<AdvancedToggle onClick={onToggleAdvancedSettings}>
						{showAdvancedSettings ? (
							<LuChevronDown size={14} />
						) : (
							<LuChevronRight size={14} />
						)}{' '}
						Settings
					</AdvancedToggle>
				</SectionContent>
			</SidebarSection>

			{showAdvancedSettings && (
				<SidebarSection>
					<SectionTitle>Flight Settings</SectionTitle>
					{/*
					 * All sliders are fully interactive and trigger recalculation
					 * via useEffect in FlightPlanner.jsx on every change.
					 *
					 * Slider ranges (hard-coded here):
					 *   Flight Height: 20–500m (configurable via FEATURE_FLAGS.MAX_FLIGHT_HEIGHT;
					 *     >120m AGL requires special authorization in EU/Norway)
					 *   Side/Front Overlap: 50–95% (standard photogrammetry range)
					 *   Speed: 2–15 m/s (15 = DJI M3E normal mode max, verified)
					 *   Flight Angle: 0–180°, step 5 (0°=N-S, 90°=E-W)
					 *   Gimbal Pitch: -90° to 0° (-90°=nadir)
					 *
					 * NOTE: terrainFollow, smartOblique, and gimbalPitch are NOT used
					 * in computeFlightPattern() — they are metadata saved to the API
					 * and applied by the drone controller at flight time.
					 *
					 * TODO(verify-with-PO): No user warning when speed + overlap
					 * combination requires a photo interval below minInterval.
					 * The code silently caps at minInterval, meaning actual overlap
					 * may be lower than displayed.
					 */}
					<SectionContent>
						<FormGroup>
							<label>
								Flight Height:{' '}
								<span className="range-value">{projectSettings.flightHeight}m</span>
							</label>
							<input
								type="range"
								min="20"
								max={FEATURE_FLAGS.MAX_FLIGHT_HEIGHT}
								value={projectSettings.flightHeight}
								onChange={e =>
									updateField('flightHeight', parseInt(e.target.value))
								}
							/>
							{projectSettings.flightHeight >
								FEATURE_FLAGS.AUTHORIZATION_HEIGHT_THRESHOLD && (
								<WarningHint>
									<LuTriangleAlert /> Above{' '}
									{FEATURE_FLAGS.AUTHORIZATION_HEIGHT_THRESHOLD}m AGL requires
									special authorization
								</WarningHint>
							)}
						</FormGroup>

						<SettingsGrid>
							<FormGroup style={{ marginBottom: 0 }}>
								<label>
									Side Overlap:{' '}
									<span className="range-value">
										{projectSettings.sideOverlap}%
									</span>
								</label>
								<input
									type="range"
									min="50"
									max="95"
									value={projectSettings.sideOverlap}
									onChange={e =>
										updateField('sideOverlap', parseInt(e.target.value))
									}
								/>
							</FormGroup>
							<FormGroup style={{ marginBottom: 0 }}>
								<label>
									Front Overlap:{' '}
									<span className="range-value">
										{projectSettings.frontOverlap}%
									</span>
								</label>
								<input
									type="range"
									min="50"
									max="95"
									value={projectSettings.frontOverlap}
									onChange={e =>
										updateField('frontOverlap', parseInt(e.target.value))
									}
								/>
							</FormGroup>
						</SettingsGrid>

						<FormGroup>
							<label>
								Speed:{' '}
								<span className="range-value">{projectSettings.speed} m/s</span>
							</label>
							<input
								type="range"
								min="2"
								max="15"
								value={projectSettings.speed}
								onChange={e => updateField('speed', parseInt(e.target.value))}
							/>
						</FormGroup>

						<FormGroup>
							<label>
								Flight Angle:{' '}
								<span className="range-value">{projectSettings.flightAngle}°</span>
							</label>
							<input
								type="range"
								min="0"
								max="180"
								step="5"
								value={projectSettings.flightAngle}
								onChange={e => updateField('flightAngle', parseInt(e.target.value))}
							/>
							<small style={{ color: '#888', fontSize: '0.75rem' }}>
								0° = North-South, 90° = East-West
							</small>
						</FormGroup>

						<FormGroup>
							<label>
								Gimbal Pitch:{' '}
								<span className="range-value">{projectSettings.gimbalPitch}°</span>
							</label>
							<input
								type="range"
								min="-90"
								max="0"
								value={projectSettings.gimbalPitch}
								onChange={e => updateField('gimbalPitch', parseInt(e.target.value))}
							/>
						</FormGroup>

						<ToggleGroup>
							<label>Terrain Following</label>
							<Toggle
								$active={projectSettings.terrainFollow}
								onClick={() =>
									updateField('terrainFollow', !projectSettings.terrainFollow)
								}
							/>
						</ToggleGroup>

						{FEATURE_FLAGS.SMART_OBLIQUE_ENABLED && (
							<ToggleGroup>
								<label>Smart Oblique</label>
								<Toggle
									$active={projectSettings.smartOblique}
									onClick={() =>
										updateField('smartOblique', !projectSettings.smartOblique)
									}
								/>
							</ToggleGroup>
						)}

						<AiModelSection
							aiModelUuid={projectSettings.aiModelUuid}
							onAiModelChange={value => updateField('aiModelUuid', value)}
							availableAiModels={availableAiModels}
							helpText={{
								selected:
									'Model runs automatically on the flight polygon after orthophoto is ready',
								none: 'No automatic detection after processing',
							}}
						/>
					</SectionContent>
				</SidebarSection>
			)}
		</>
	);
};

export default memo(WorkflowSetup);
